<script lang="ts">
  import { onDestroy, onMount } from 'svelte';
  import { get, writable } from 'svelte/store';
  import type { BackendActor } from '../actor';
  import { assets } from '../assets';
  import type { EventEntry, ItemWithId, PlayerStance } from '../backend';
  import { enumIs } from '../enums';
  import { EventScheduler, GameTime } from '../eventScheduler';
  import { events, gameState, type InGameState } from '../gameState';
  import { unreachable } from '../unreachable';
  import Pistol from './tooltips/Pistol.svelte';
  import Knife from './tooltips/Knife.svelte';
  import { guide } from '../guide';
  import Armor from './tooltips/Armor.svelte';
  import Boots from './tooltips/Boots.svelte';

  export let actor: BackendActor;
  // export let myPrincipal: string;
  let state: InGameState;

  let leaving = false;

  let tabShown = false;

  let canvas: HTMLCanvasElement;
  let pointerLocked = false;

  let usingSlotAt: number | null = null;
  let attackMoving = false;

  let tooltip = writable<
    | {
        type: 'item';
        item: ItemWithId;
        x: number;
        y: number;
        extra: string;
      }
    | {
        type: 'stance';
        stance: PlayerStance;
        x: number;
        y: number;
        extra: string;
      }
    | null
  >(null);

  // let cursor = new Cursor(0,0);
  // let cursor: { x: number; y: number } | null = null;
  // let mapOffset: { x: number; y: number } | null = null;

  // const MAP_WIDTH = 40;
  // let cursorIsOverInventorySlot: number | false;

  let cursorIsOver:
    | {
        type: 'enemy';
      }
    | {
        type: 'inventorySlot';
        slot: number;
      }
    | {
        type: 'stance';
        stance: PlayerStance;
      }
    | { type: 'none' } = { type: 'none' };

  let lastPaint = 0;

  const gameTime = new GameTime();
  const eventScheduler = new EventScheduler();

  let isMobile = window.matchMedia('(pointer: coarse)').matches;

  // let cur

  // function toCanvasCoords(x: number, y: number): { x: number; y: number } {
  //   return {
  //     x: x * TILE_SIZE - mapOffset.x,
  //     y: y * TILE_SIZE - mapOffset.y,
  //   };
  // }

  function centerView() {
    if (!canvas) return;

    state.renderer.setViewByGameCoords(
      state.game.ownPlayer.loc.x,
      state.game.ownPlayer.loc.y,
      canvas
    );

    // mapOffset = {
    //   x: playerPosition.x * TILE_SIZE - canvas.width / 2,
    //   y: playerPosition.y * TILE_SIZE - canvas.height / 2,
    // };
  }

  function paint() {
    if (!canvas) {
      return;
    }
    handle = requestAnimationFrame(paint);

    if (Date.now() - lastPaint < 16) {
      return;
    }

    lastPaint = Date.now();

    state.renderer.checkEdgeScroll(canvas);

    const context = canvas.getContext('2d');

    if (state.game.ownPlayer.health <= 0) {
      context.filter = 'grayscale(1)';
    }

    state.renderer.drawMap(context);
    state.renderer.drawBrushes(context, state.game.ownPlayer.loc);
    state.renderer.drawHouses(context, state.game.ownPlayer.loc);

    if (enumIs(state.game.gameType, 'Regular')) {
      state.renderer.drawCenterMarker(context);
    }

    const recentEvents = get(events);
    if (!recentEvents) {
      return;
    }
    const lastGameTime = gameTime.getGameTime();

    gameTime.tick(
      eventScheduler.timeUnitsLeft(lastGameTime),
      eventScheduler.getTimeUnitsToProcessTillNextUpdate()
    );
    const currentGameTime = gameTime.getGameTime();

    const relevantEvents = eventScheduler.getRelevantEvents(
      lastGameTime,
      currentGameTime
    );

    if (relevantEvents.length > 0) {
      state.game.updateObjects(currentGameTime, relevantEvents);
    }
    if (enumIs(state.game.gameType, 'Tutorial')) {
      $guide.advance(state.game, relevantEvents, state.renderer);
    }

    // console.log(state.game.ownPlayer.inventory);

    if (isMobile) {
      centerView();
    }

    cursorIsOver = { type: 'none' };

    for (const enemy of state.game.otherPlayers) {
      enemy.draw(context, state.renderer);

      if (enemy.loc) {
        const enemyLoc = state.renderer.toCanvasCoords(
          enemy.loc.x,
          enemy.loc.y
        );

        // check if mouse is over enemy
        if (
          state.renderer.cursor.x > enemyLoc.x - 16 &&
          state.renderer.cursor.x < enemyLoc.x + 16 &&
          state.renderer.cursor.y > enemyLoc.y - 16 &&
          state.renderer.cursor.y < enemyLoc.y + 16
        ) {
          context.strokeStyle = 'red';
          context.lineWidth = 2;
          context.strokeRect(enemyLoc.x - 16, enemyLoc.y - 16, 32, 32);
          cursorIsOver = { type: 'enemy' };
        }
      }
    }

    if (state.game.ownPlayer.health > 0) {
      state.game.ownPlayer.queue.draw(
        context,
        state.renderer,
        state.game.ownPlayer.loc
      );
    }

    state.game.drawItems(context, state.renderer);

    state.game.ownPlayer.draw(context, state.renderer);

    state.game.drawGameObjects(context, state.renderer);

    for (const p of state.game.particles) {
      p.draw(context, Date.now(), state.renderer);
    }

    if (enumIs(state.game.gameType, 'Regular')) {
      state.game.drawDeathZone(context, state.renderer);
    }
    if (state.game.ownPlayer.health > 0) {
      state.game.drawFogOfWar(context, state.renderer);
    }

    if (state.game.hasPlayerWon()) {
      // draw in large text to the middle: You won the game!

      context.fillStyle = 'rgba(0,0,0,0.5)';
      context.fillRect(0, 0, canvas.width, canvas.height);

      context.fillStyle = 'white';
      context.font = 'bold 48px sans-serif';
      context.textAlign = 'center';
      context.textBaseline = 'middle';
      context.fillText(
        'You won the game!',
        canvas.width / 2,
        canvas.height / 2
      );
    }

    const hudRegions = state.game.drawHud(context, state.renderer);

    if (enumIs(state.game.gameType, 'Tutorial')) {
      $guide.draw(context, state.game, state.renderer);
    }

    state.game.drawMessages(context);
    let tooltipToShow = false;
    for (const item of state.game.visibleItems) {
      const itemLoc = state.renderer.toCanvasCoords(item.loc.x, item.loc.y);
      const currentTooltip = get(tooltip);
      if (
        state.renderer.cursor.x > itemLoc.x - 16 &&
        state.renderer.cursor.x < itemLoc.x + 16 &&
        state.renderer.cursor.y > itemLoc.y - 16 &&
        state.renderer.cursor.y < itemLoc.y + 16
      ) {
        tooltipToShow = true;
        if (
          currentTooltip &&
          currentTooltip.type === 'item' &&
          currentTooltip.item === item
        ) {
          continue;
        } else {
          tooltip.set({
            x: itemLoc.x,
            y: itemLoc.y,
            item,
            type: 'item',
            extra: '',
          });
        }
      }
    }

    if (!tooltipToShow) {
      for (const region of hudRegions) {
        if (
          state.renderer.cursor.x > region.x &&
          state.renderer.cursor.x < region.x + region.w &&
          state.renderer.cursor.y > region.y &&
          state.renderer.cursor.y < region.y + region.h
        ) {
          tooltipToShow = true;

          if (region.type === 'inventorySlot') {
            cursorIsOver = { type: 'inventorySlot', slot: region.slot };
            tooltip.set({
              x: region.x + region.w / 2,
              y: region.y - 8,
              item: region.item,
              type: 'item',
              extra: `Press shift+${region.slot + 1} to drop`,
            });
          } else if (region.type === 'weaponSlot') {
            tooltip.set({
              x: region.x + region.w / 2,
              y: region.y - 8,
              item: region.item,
              type: 'item',
              extra: `Press d to drop`,
            });
          } else if (region.type === 'stance') {
            tooltip.set({
              x: region.x + region.w / 2,
              y: region.y - 8,
              type: 'stance',
              stance: region.stance,
              extra:
                enumIs(region.stance, 'Defensive') ||
                !!state.game.ownPlayer.weapon
                  ? `Press w to switch`
                  : `You need a weapon!`,
            });
            cursorIsOver = {
              type: 'stance',
              stance: region.stance,
            };
          } else {
            unreachable(region);
          }

          break;
        }
      }
    }

    if (!tooltipToShow) {
      tooltip.set(null);
    }

    if (tabShown) {
      state.game.drawTabInfo(context);
    }
    if (!isMobile) {
      if (usingSlotAt !== null) {
        // const { x: dx, y: dy } = state.renderer.canvasToWorldSpace(
        //   state.renderer.cursor.x,
        //   state.renderer.cursor.y
        // );

        // draw assets.items.grenade at cursor pos

        const item = state.game.ownPlayer.inventory.slots[usingSlotAt][0];
        if (item && enumIs(item.item_type, 'Grenade')) {
          const startLoc = state.renderer.toCanvasCoords(
            state.game.ownPlayer.loc.x,
            state.game.ownPlayer.loc.y
          );
          // draw red line from final loc to cursor

          context.strokeStyle = 'red';
          context.lineWidth = 2;
          context.beginPath();
          context.moveTo(startLoc.x, startLoc.y);
          context.lineTo(state.renderer.cursor.x, state.renderer.cursor.y);
          context.stroke();

          context.drawImage(
            assets.images.items.grenade,
            state.renderer.cursor.x - state.renderer.tileSize / 2,
            state.renderer.cursor.y - state.renderer.tileSize / 2,
            state.renderer.tileSize,
            state.renderer.tileSize
          );
        }
      } else if (attackMoving) {
        const finalLoc = state.renderer.toCanvasCoords(
          state.game.ownPlayer.queue.finalLoc.x,
          state.game.ownPlayer.queue.finalLoc.y
        );
        // draw red line from final loc to cursor

        context.strokeStyle = 'red';
        context.lineWidth = 2;
        context.beginPath();
        context.moveTo(finalLoc.x, finalLoc.y);
        context.lineTo(state.renderer.cursor.x, state.renderer.cursor.y);
        context.stroke();

        state.renderer.cursor.draw(context, state.renderer, 'red');
      } else {
        state.renderer.cursor.draw(context, state.renderer);
      }
    }
  }

  const onMouseMove = (e: MouseEvent) => {
    if (pointerLocked) {
      state.renderer.cursor.update(
        e.movementX,
        e.movementY,
        canvas.width,
        canvas.height
      );
    }
  };

  function click(e: MouseEvent) {
    if (pointerLocked || isMobile) {
      if (isMobile) {
        state.renderer.cursor.x =
          e.clientX - canvas.getBoundingClientRect().left;
        state.renderer.cursor.y =
          e.clientY - canvas.getBoundingClientRect().top;
      }

      // right click cancels using item
      if (usingSlotAt !== null) {
        if (e.which === 3) {
          // cancel action
          usingSlotAt = null;
          return;
        }
        useItemAt(usingSlotAt);
        usingSlotAt = null;
        return;
      }

      if (attackMoving) {
        attackMoving = false;
        return guard();
      }

      if (cursorIsOver.type === 'enemy') {
        return guard();
      } else if (cursorIsOver.type === 'inventorySlot') {
        if (e.shiftKey) {
          return dropItemFromInventory(cursorIsOver.slot);
        } else {
          return useItem(cursorIsOver.slot);
        }
      } else if (cursorIsOver.type === 'stance') {
        return switchStanceTo(cursorIsOver.stance);
      } else if (enumIs(state.game.ownPlayer.stance, 'Offensive')) {
        return guard();
      } else {
        return move(e);
      }
    } else {
      document.addEventListener('pointerlockchange', onLockChange);
      canvas.requestPointerLock();
      // (canvas.requestPointerLock as any)({ unadjustedMovement: true });

      state.renderer.cursor.x = e.clientX - canvas.getBoundingClientRect().left;
      state.renderer.cursor.y = e.clientY - canvas.getBoundingClientRect().top;

      if (enumIs(state.game.gameType, 'Tutorial')) {
        $guide.canvasClicked();
      }
    }
  }

  async function guardAt(x: number, y: number) {
    const actionWithId = state.game.ownPlayer.queue.recordAction({
      Guard: { to: { x, y }, path: [] },
    });
    await actor.act(actionWithId);
  }
  async function guard() {
    if (state.game.ownPlayer.weapon) {
      state.game.ownPlayer.stance = { Offensive: null };
    }
    const { x, y } = state.renderer.canvasToWorldSpace(
      state.renderer.cursor.x,
      state.renderer.cursor.y
    );
    guardAt(x, y);
  }

  async function useItemAt(slot: number) {
    const { x: dx, y: dy } = state.renderer.canvasToWorldSpace(
      state.renderer.cursor.x,
      state.renderer.cursor.y
    );

    const actionWithId = state.game.ownPlayer.queue.recordAction(
      {
        UseItemAt: {
          slot,
          at: {
            x: dx,
            y: dy,
          },
        },
      },
      true
    );
    await actor.act(actionWithId);
  }

  async function dropWeapon() {
    const actionWithId = state.game.ownPlayer.queue.recordAction(
      {
        DropWeapon: null,
      },
      true
    );
    await actor.act(actionWithId);
  }

  function switchStanceTo(to: PlayerStance) {
    if (enumIs(to, 'Offensive') && !state.game.ownPlayer.weapon) {
      return;
    }

    if (
      enumIs(to, 'Offensive') &&
      !state.game.ownPlayer.queue
        .getQueue()
        .find((a) => enumIs(a.action, 'Guard'))
    ) {
      guardAt(
        state.game.ownPlayer.queue.finalLoc.x,
        state.game.ownPlayer.queue.finalLoc.y
      );
    }

    state.game.ownPlayer.stance = to;
  }

  function switchStance() {
    if (enumIs(state.game.ownPlayer.stance, 'Defensive')) {
      switchStanceTo({ Offensive: null });
    } else if (enumIs(state.game.ownPlayer.stance, 'Offensive')) {
      switchStanceTo({ Defensive: null });
    } else {
      unreachable(state.game.ownPlayer.stance);
    }

    // if (enumIs(state.game.ownPlayer.stance, 'Defensive')) {
    //   await actor.act(
    //     state.game.ownPlayer.queue.recordAction({
    //       ChangeStanceTo: {
    //         Offensive: null,
    //       },
    //     })
    //   );
    // } else if (enumIs(state.game.ownPlayer.stance, 'Offensive')) {
    //   await actor.act(
    //     state.game.ownPlayer.queue.recordAction({
    //       ChangeStanceTo: {
    //         Defensive: null,
    //       },
    //     })
    //   );
    // } else {
    //   unreachable(state.game.ownPlayer.stance);
    // }
  }

  async function stop() {
    const actionWithId = state.game.ownPlayer.queue.recordAction({
      Stop: null,
    });
    await actor.act(actionWithId);
  }

  async function useItem(slot: number) {
    const slotItem = state.game.ownPlayer.inventory.slots[slot][0];
    if (slotItem && enumIs(slotItem.item_type, 'Grenade')) {
      if (usingSlotAt === slot) {
        usingSlotAt = null;
        return;
      }
      attackMoving = false;
      usingSlotAt = slot;
      return;
    }

    const actionWithId = state.game.ownPlayer.queue.recordAction(
      {
        UseItem: {
          slot,
        },
      },
      true
    );
    await actor.act(actionWithId);
  }

  async function dropItemFromInventory(slot: number) {
    const actionWithId = state.game.ownPlayer.queue.recordAction({
      DropItemFromInventory: {
        slot,
      },
    });
    await actor.act(actionWithId);
  }

  async function move(e: MouseEvent) {
    const { x: dx, y: dy } = state.renderer.canvasToWorldSpace(
      state.renderer.cursor.x,
      state.renderer.cursor.y
    );
    const actionWithId = state.game.ownPlayer.queue.recordAction({
      Move: {
        to: {
          x: dx,
          y: dy,
        },
        path: [],
      },
    });
    await actor.act(actionWithId);
  }

  function onKeyDown(e: KeyboardEvent) {
    if (e.key === ' ') {
      centerView();
    }
    if (e.key === 'a') {
      attackMoving = true;
      usingSlotAt = null;
    }

    if (e.key === 'd') {
      dropWeapon();
    }

    if (e.key === 'w') {
      switchStance();
    }

    if (e.key === 's') {
      stop();
    }

    switch (e.code) {
      case 'Digit1':
        e.shiftKey ? dropItemFromInventory(0) : useItem(0);
        break;
      case 'Digit2':
        e.shiftKey ? dropItemFromInventory(1) : useItem(1);
        break;
      case 'Digit3':
        e.shiftKey ? dropItemFromInventory(2) : useItem(2);
        break;
      case 'Digit4':
        e.shiftKey ? dropItemFromInventory(3) : useItem(3);
        break;
      case 'Digit5':
        e.shiftKey ? dropItemFromInventory(4) : useItem(4);
        break;
      case 'Digit6':
        e.shiftKey ? dropItemFromInventory(5) : useItem(5);
        break;
    }

    if (e.key === 'Tab' && pointerLocked) {
      e.preventDefault();
      e.stopPropagation();
      tabShown = true;
    }
  }

  function onKeyUp(e: KeyboardEvent) {
    if (e.key === 'Tab' && pointerLocked) {
      e.preventDefault();
      e.stopPropagation();
      tabShown = false;
    }
  }

  function onLockChange(e: Event) {
    if (document.pointerLockElement) {
      pointerLocked = true;
      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('keydown', onKeyDown);
      document.addEventListener('keyup', onKeyUp);
    } else {
      pointerLocked = false;
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('keyup', onKeyUp);
    }
  }

  let handle: ReturnType<typeof requestAnimationFrame>;

  let gameStateUnsub: () => void;
  let eventsUnsub: () => void;

  function processNewEvents(events: EventEntry[]) {
    gameTime.markDataArrived();
    eventScheduler.addEvents(events);
    eventScheduler.markTimeUnitsToProcessTillNextUpdate(gameTime.getGameTime());
    eventScheduler.cleanEvents(gameTime.getGameTime());

    const maybePlayerEvents = events.find(
      (e) =>
        enumIs(e.data, 'Player') &&
        e.data.Player.player.toText() === state.player.principal.toText()
    );
    if (maybePlayerEvents && enumIs(maybePlayerEvents.data, 'Player')) {
      state.game.ownPlayer.queue.updateWith(
        maybePlayerEvents.data.Player.queue[0],
        maybePlayerEvents.data.Player.last_processed_action_id
      );
    }
  }

  function onResize() {
    if (!canvas) return;
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight - 32;
    centerView();
  }

  onMount(() => {
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight - 32;

    window.addEventListener('resize', onResize);

    gameStateUnsub = gameState.subscribe((gameState) => {
      if (gameState.state === 'in-game') {
        state = gameState;
        centerView();
        state.renderer.cursor.x = canvas.width / 2;
        state.renderer.cursor.y = canvas.height / 2;
      }
    });

    const startingEvents = get(events);
    if (startingEvents && startingEvents.events.length > 0) {
      gameTime.lastGameTime = startingEvents.events[0].start_time;
      processNewEvents(startingEvents.events);
    }

    eventsUnsub = events.subscribe((es) => {
      processNewEvents(es.events);
    });
    paint();
  });

  onDestroy(() => {
    cancelAnimationFrame(handle);
    gameStateUnsub();
    eventsUnsub();
    document.removeEventListener('mousemove', onMouseMove);
    document.removeEventListener('keydown', onKeyDown);
    document.removeEventListener('pointerlockchange', onLockChange);
    document.removeEventListener('keyup', onKeyUp);
    window.removeEventListener('resize', onResize);
  });

  async function leaveGame() {
    leaving = true;
    try {
      const actionWithId = state.game.ownPlayer.queue.recordAction({
        Leave: null,
      });
      await actor.act(actionWithId);
    } catch {
    } finally {
      leaving = false;
    }
  }
</script>

<!-- {state.player.loc.x}x{state.player.loc.y} -->

<canvas bind:this={canvas} on:click={click} class="fixed inset-0" />

<div
  class="fixed bottom-0 left-0 right-0 bg-slate-500 h-8 flex items-center gap-4 text-xs px-3"
>
  <span class="font-bold uppercase tracking-wider">Controls</span>
  <span class="flex items-center gap-x-1">
    <kbd class="kbd kbd-xs">w</kbd> Switch stance
  </span>
  <span class="flex items-center gap-x-1">
    <kbd class="kbd kbd-xs">a</kbd> Move & Attack
  </span>
  <span class="flex items-center gap-x-1">
    <kbd class="kbd kbd-xs">s</kbd> Stop
  </span>
  <span class="flex items-center gap-x-1">
    <kbd class="kbd kbd-xs">d</kbd> Drop weapon
  </span>
  <span class="flex items-center gap-x-1">
    <kbd class="kbd kbd-xs">tab</kbd> Score
  </span>
</div>

<div class="fixed top-0 left-0 p-4">
  <button
    on:click={() => leaveGame()}
    class="btn btn-xs btn-primary {leaving && 'loading'}"
    disabled={leaving}>Leave game</button
  >
</div>

{#if $tooltip}
  <div
    style="left:{$tooltip.x}px; top: {$tooltip.y}px"
    class="fixed w-60 -translate-x-1/2 -translate-y-full bg-cyan-700/80 text-white p-4 rounded-xl"
  >
    {#if $tooltip.type == 'item'}
      {#if enumIs($tooltip.item.item_type, 'Knife')}
        <Knife knife={$tooltip.item.item_type.Knife} extra={$tooltip.extra} />
      {:else if enumIs($tooltip.item.item_type, 'MedKit')}
        <h2 class="font-black mb-2 text-xl">MedKit</h2>
        <p class="text-white/80">
          Restores {$tooltip.item.item_type.MedKit.health} health.
        </p>
        {#if $tooltip.extra}<p class="text-white/80 text-sm mt-4">
            {$tooltip.extra}
          </p>{/if}
      {:else if enumIs($tooltip.item.item_type, 'Pistol')}
        <Pistol
          pistol={$tooltip.item.item_type.Pistol}
          extra={$tooltip.extra}
        />
      {:else if enumIs($tooltip.item.item_type, 'Boots')}
        <Boots boots={$tooltip.item.item_type.Boots} extra="" />
      {:else if enumIs($tooltip.item.item_type, 'Grenade')}
        <h2 class="font-black mb-2 text-xl">Grenade</h2>
        <p class="text-white/80">
          Explodes after throwing at a target location, dealing {$tooltip.item.item_type.Grenade.damage.toFixed(
            0
          )} damage.
        </p>
        {#if $tooltip.extra}<p class="text-white/80 text-sm mt-4">
            {$tooltip.extra}
          </p>{/if}
      {:else if enumIs($tooltip.item.item_type, 'Armor')}
        <Armor armor={$tooltip.item.item_type.Armor} extra="" />
      {:else if enumIs($tooltip.item.item_type, 'EnergyDrink')}
        <h2 class="font-black mb-2 text-xl">Energy Drink</h2>
        <p class="text-white/80">
          Temporarily increases movement and attack speed.
        </p>

        {#if $tooltip.extra}<p class="text-white/80 text-sm mt-4">
            {$tooltip.extra}
          </p>{/if}
      {:else}
        {unreachable($tooltip.item.item_type)}
      {/if}
    {:else if $tooltip.type === 'stance'}
      {#if enumIs($tooltip.stance, 'Defensive')}
        <h2 class="font-black mb-2 text-xl">Defensive Stance</h2>
        <p class="text-white/80">
          Move without attacking enemies. Use when you need to reposition
          quickly or flee to safety.
        </p>
        {#if $tooltip.extra}<p class="text-white/80 text-sm mt-4">
            {$tooltip.extra}
          </p>{/if}
      {:else if enumIs($tooltip.stance, 'Offensive')}
        <h2 class="font-black mb-2 text-xl">Offensive Stance</h2>
        <p class="text-white/80">
          Attack enemies on sight. Use to ambush enemies, defend a position, or
          chase down a fleeing enemy.
        </p>
        {#if $tooltip.extra}<p class="text-white/80 text-sm mt-4">
            {$tooltip.extra}
          </p>{/if}
      {:else}
        {unreachable($tooltip.stance)}
      {/if}
    {:else}
      {unreachable($tooltip)}
    {/if}
  </div>
{/if}
